// src/legal/markdown.ts: reads a builder's privacy notice (config/legal/<tenant>/privacy.md, docs/legal/) into a
// small document model for /privacy. Only what the notice uses: a # title, a bold subtitle, "Last updated", a
// "> **The short version**" quote, ## sections, bold-only lines as subheads, paragraphs, "-" lists (one nested
// level, two spaces), pipe tables and "---" rules. Inline: **bold**, `code` and [TO CONFIRM: ...] placeholders,
// which stay visible. Text only: nothing is ever rendered as HTML.

export type Inline = { kind: "text" | "bold" | "code" | "tbc"; text: string; children?: Inline[] };
export type ListItem = { text: Inline[]; children: ListItem[] };
export type Block =
  | { kind: "p"; text: Inline[] }
  | { kind: "sub"; text: Inline[] }
  | { kind: "ul"; items: ListItem[] }
  | { kind: "table"; head: Inline[][]; rows: Inline[][][] };
export type Section = { id: string; heading: string; blocks: Block[] };
export type LegalDoc = {
  title: string;
  subtitle: string | null;
  /** The text after "Last updated:" (may itself be a [TO CONFIRM] placeholder). */
  updated: Inline[] | null;
  /** The quoted summary at the top, when there is one. */
  short: { heading: string; blocks: Block[] } | null;
  sections: Section[];
};

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[TO CONFIRM[^\]]*\])/g;

/** "**Bold** with `code` and [TO CONFIRM: x]" to inline runs. Bold runs are read again for placeholders and code. */
export function inline(src: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of src.matchAll(INLINE_RE)) {
    const tok = m[0];
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", text: src.slice(last, at) });
    if (tok.startsWith("**")) {
      const text = tok.slice(2, -2);
      out.push({ kind: "bold", text, children: inline(text) });
    } else if (tok.startsWith("`")) out.push({ kind: "code", text: tok.slice(1, -1) });
    else out.push({ kind: "tbc", text: tok.slice(1, -1).replace(/^TO CONFIRM:?\s*/, "") });
    last = at + tok.length;
  }
  if (last < src.length) out.push({ kind: "text", text: src.slice(last) });
  return out;
}

/** Plain text of inline runs (for ids and labels). */
export function plain(runs: Inline[]): string {
  return runs.map((r) => (r.kind === "tbc" ? `[TO CONFIRM: ${r.text}]` : r.text)).join("");
}

export function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const BOLD_LINE = /^\*\*([^*]+)\*\*(\s+\[TO CONFIRM[^\]]*\])?$/;
const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

/** Block-level parse of a run of lines (a section body, or the quote's lines without their "> "). */
export function blocks(lines: string[]): Block[] {
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const t = line.trim();
    if (!t || /^-{3,}$/.test(t)) {
      i++;
      continue;
    }
    if (t.startsWith("|")) {
      const rows: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith("|")) rows.push(lines[i++]!);
      const [head, , ...body] = rows;
      out.push({ kind: "table", head: cells(head ?? "").map(inline), rows: body.map((r) => cells(r).map(inline)) });
      continue;
    }
    if (/^- /.test(t) && !/^\s/.test(line)) {
      const items: ListItem[] = [];
      while (i < lines.length) {
        const l = lines[i]!;
        if (/^- /.test(l)) items.push({ text: inline(l.slice(2).trim()), children: [] });
        else if (/^\s{2,}- /.test(l) && items.length) items[items.length - 1]!.children.push({ text: inline(l.trim().slice(2).trim()), children: [] });
        else break;
        i++;
      }
      out.push({ kind: "ul", items });
      continue;
    }
    const bold = t.match(BOLD_LINE);
    if (bold) {
      out.push({ kind: "sub", text: inline(t) });
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i]!.trim();
      if (!l || /^-{3,}$/.test(l) || l.startsWith("|") || /^- /.test(l) || (para.length > 0 && BOLD_LINE.test(l))) break;
      para.push(l);
      i++;
    }
    out.push({ kind: "p", text: inline(para.join(" ")) });
  }
  return out;
}

/** The whole notice. Everything before the first ## is the title block; each ## starts a section. */
export function parseLegal(md: string): LegalDoc {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const doc: LegalDoc = { title: "", subtitle: null, updated: null, short: null, sections: [] };
  let i = 0;
  const quote: string[] = [];
  for (; i < lines.length; i++) {
    const t = lines[i]!.trim();
    if (t.startsWith("## ")) break;
    if (t.startsWith("# ")) doc.title = t.slice(2).trim();
    else if (t.startsWith(">")) quote.push(t.replace(/^>\s?/, ""));
    else if (/^last updated:/i.test(t)) doc.updated = inline(t.replace(/^last updated:\s*/i, ""));
    else if (!doc.subtitle && BOLD_LINE.test(t)) doc.subtitle = t.replace(/^\*\*|\*\*$/g, "");
  }
  if (quote.length) {
    const qb = blocks(quote);
    const first = qb[0];
    const heading = first && first.kind === "sub" ? plain(first.text).replace(/^\s+|\s+$/g, "") : "In short";
    doc.short = { heading: heading.replace(/\*\*/g, ""), blocks: first && first.kind === "sub" ? qb.slice(1) : qb };
  }
  const seen = new Set<string>();
  let cur: { heading: string; lines: string[] } | null = null;
  const flush = () => {
    if (!cur) return;
    let id = slug(cur.heading) || "section";
    while (seen.has(id)) id += "-2";
    seen.add(id);
    doc.sections.push({ id, heading: cur.heading, blocks: blocks(cur.lines) });
  };
  for (; i < lines.length; i++) {
    const l = lines[i]!;
    if (l.startsWith("## ")) {
      flush();
      cur = { heading: l.slice(3).trim(), lines: [] };
    } else if (cur) cur.lines.push(l);
  }
  flush();
  return doc;
}
